/**
 * 预测模块状态管理
 * 使用 Zustand + persist 统一管理 CLOB 凭证状态
 * 凭证持久化到 localStorage，保留 24 小时
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ApiKeyCreds } from '@polymarket/clob-client-v2';
import { ethers } from 'ethers5';
import { createPublicClient, http, erc20Abi } from 'viem';
import { polygon } from 'viem/chains';
import { deriveUserCredentials, CLOB_API_URL } from '@/lib/polymarket-clob';
import { getFeeConfig, type FeeConfig } from '@/lib/predict-api';
import {
  CONTRACTS,
  getAllCollateralAllowances,
  getAllCtfApprovals,
  type UsdcAllowance,
  type CtfApproval,
} from '@/lib/predict-approvals';
import { CHAIN_RPC_URLS } from '@/lib/rpc-utils';

// Signer 类型与 polymarket-clob.ts 保持一致
type Signer = ethers.Signer;

// 凭证有效期：24 小时
const CREDENTIALS_TTL_MS = 24 * 60 * 60 * 1000;

export interface PredictState {
  // CLOB 凭证
  credentials: ApiKeyCreds | null;
  // 凭证过期时间戳
  credentialsExpireAt: number | null;
  // 手续费配置（从接口获取）
  feeConfig: FeeConfig | null;
  // CLOB 版本(应用启动时拉取,1=V1 后端 / 2=V2 后端)
  version: number | null;
  versionUpdatedAt: number | null;
  // 抵押币 + CTF 授权状态(全局单例,任何组件 useApprovals 都读这份)
  usdcAllowances: UsdcAllowance[];
  ctfApprovals: CtfApproval[];
  approvalsLoading: boolean;
  approvalsError: string | null;
  // Safe 钱包代币余额(全局单例,V1/V2 双余额并存,展示用 selector 按 version 选)
  pusdBalance: bigint;
  usdcEBalance: bigint;
  balancesUpdatedAt: number | null;
  balancesLoading: boolean;
  balancesError: string | null;
  // 用户在 UpgradeBeforeTradeModal 勾选"24h 不再提醒"的过期时间戳(ms),null=未勾过
  upgradeReminderSkipUntil: number | null;
  // debug:CLOB host 覆盖(localStorage 持久化)。默认 null = 用 polymarket-clob 的 DEFAULT_CLOB_HOST
  // 设置后需刷新页面生效(模块加载时一次性读)
  clobHostOverride: string | null;
  // V2 升级欢迎弹窗:V2 模式下首次进入 /predict 弹一次,看过即 true。debug 可清缓存重弹
  v2WelcomeShown: boolean;

  // Actions
  setCredentials: (creds: ApiKeyCreds | null) => void;
  getOrCreateCredentials: (signer: Signer) => Promise<ApiKeyCreds>;
  fetchFeeConfig: () => Promise<void>;
  setVersion: (v: number) => void;
  fetchVersion: () => Promise<number>;
  refreshApprovals: (safeAddress: string) => Promise<void>;
  refreshBalances: (safeAddress: string) => Promise<void>;
  setUpgradeReminderSkipUntil: (untilMs: number | null) => void;
  setClobHostOverride: (url: string | null) => void;
  setV2WelcomeShown: (shown: boolean) => void;
  clearAll: () => void;
}

export const usePredictStore = create<PredictState>()(
  persist(
    (set, get) => ({
      // 初始状态
      credentials: null,
      credentialsExpireAt: null,
      feeConfig: null,
      version: null,
      versionUpdatedAt: null,
      usdcAllowances: [],
      ctfApprovals: [],
      approvalsLoading: false,
      approvalsError: null,
      pusdBalance: BigInt(0),
      usdcEBalance: BigInt(0),
      balancesUpdatedAt: null,
      balancesLoading: false,
      balancesError: null,
      upgradeReminderSkipUntil: null,
      clobHostOverride: null,
      v2WelcomeShown: false,

      // 设置 CLOB 凭证（同时设置 24 小时过期时间）
      setCredentials: (creds) => {
        const expireAt = creds ? Date.now() + CREDENTIALS_TTL_MS : null;
        console.log('[PredictStore] setCredentials:', creds ? 'SET' : 'CLEAR', expireAt ? `expires at ${new Date(expireAt).toISOString()}` : '');
        set({ credentials: creds, credentialsExpireAt: expireAt });
      },

      // 获取或创建凭证（检查过期，过期则重新派生）
      getOrCreateCredentials: async (signer) => {
        const state = get();

        // 检查是否有有效凭证（未过期）
        if (state.credentials && state.credentialsExpireAt && Date.now() < state.credentialsExpireAt) {
          console.log('[PredictStore] Using cached credentials');
          return state.credentials;
        }

        // 过期或不存在，重新派生
        console.log('[PredictStore] Credentials expired or missing, deriving new...');
        const creds = await deriveUserCredentials(signer);
        const expireAt = Date.now() + CREDENTIALS_TTL_MS;
        set({ credentials: creds, credentialsExpireAt: expireAt });
        return creds;
      },

      // 获取手续费配置
      fetchFeeConfig: async () => {
        const config = await getFeeConfig();
        if (config) {
          console.log('[PredictStore] feeConfig loaded:', config);
          set({ feeConfig: config });
        }
      },

      // 直接设置 version(供启动外的场景使用,如下单成功后强制刷新)
      setVersion: (v) => {
        console.log('[PredictStore] setVersion:', v);
        set({ version: v, versionUpdatedAt: Date.now() });
      },

      // 拉取 CLOB version,纯 fetch 不依赖 SDK;失败时按 host 与 cutover 日期 fallback
      fetchVersion: async () => {
        try {
          const res = await fetch(`${CLOB_API_URL}/version`);
          const data = await res.json();
          const v = data?.version ?? 2;
          console.log('[PredictStore] fetchVersion success:', v);
          set({ version: v, versionUpdatedAt: Date.now() });
          return v;
        } catch (err) {
          // fallback:V2 host 直接判 2;V1 host 按 04-28 11:00 UTC 切换日期
          const CUTOVER = Date.UTC(2026, 3, 28, 11, 0);
          const fallback = CLOB_API_URL.includes('clob-v2') ? 2 : Date.now() >= CUTOVER ? 2 : 1;
          console.warn('[PredictStore] fetchVersion failed, using fallback:', fallback, err);
          set({ version: fallback, versionUpdatedAt: Date.now() });
          return fallback;
        }
      },

      // 拉取链上授权状态(全局单例,任何地方调都更新同一份 state)
      // 按 store.version 路由 V1/V2 spenders/operators 清单
      refreshApprovals: async (safeAddress: string) => {
        if (!safeAddress) {
          set({ usdcAllowances: [], ctfApprovals: [], approvalsError: null });
          return;
        }
        const version = get().version ?? 1;
        set({ approvalsLoading: true, approvalsError: null });
        try {
          console.log('[PredictStore] refreshApprovals', safeAddress, 'version:', version);
          const [usdc, ctf] = await Promise.all([
            getAllCollateralAllowances(safeAddress, version),
            getAllCtfApprovals(safeAddress, version),
          ]);
          set({ usdcAllowances: usdc, ctfApprovals: ctf, approvalsLoading: false });
        } catch (err) {
          console.error('[PredictStore] refreshApprovals failed:', err);
          set({
            approvalsError: err instanceof Error ? err.message : 'Failed to fetch approvals',
            approvalsLoading: false,
          });
        }
      },

      // 拉链上 Safe 钱包代币余额(同时拉 pUSD + USDC.e,V1/V2 双余额并存)
      // 任何 wrap/unwrap/充值/提现成功后都该调一次,保证全局展示同步
      refreshBalances: async (safeAddress: string) => {
        if (!safeAddress) {
          set({ pusdBalance: BigInt(0), usdcEBalance: BigInt(0), balancesError: null });
          return;
        }
        set({ balancesLoading: true, balancesError: null });
        try {
          const publicClient = createPublicClient({
            chain: polygon,
            transport: http(CHAIN_RPC_URLS[137]),
          });
          const [pusd, usdcE] = await Promise.all([
            publicClient.readContract({
              address: CONTRACTS.pusd as `0x${string}`,
              abi: erc20Abi,
              functionName: 'balanceOf',
              args: [safeAddress as `0x${string}`],
            }),
            publicClient.readContract({
              address: CONTRACTS.usdc as `0x${string}`,
              abi: erc20Abi,
              functionName: 'balanceOf',
              args: [safeAddress as `0x${string}`],
            }),
          ]);
          console.log('[PredictStore] refreshBalances pUSD:', pusd, 'USDC.e:', usdcE);
          set({
            pusdBalance: pusd as bigint,
            usdcEBalance: usdcE as bigint,
            balancesUpdatedAt: Date.now(),
            balancesLoading: false,
          });
        } catch (err) {
          console.error('[PredictStore] refreshBalances failed:', err);
          set({
            balancesError: err instanceof Error ? err.message : 'Failed to fetch balances',
            balancesLoading: false,
          });
        }
      },

      // 用户在 UpgradeBeforeTradeModal 勾选"24h 不再提醒",传 Date.now()+duration 时间戳
      setUpgradeReminderSkipUntil: (untilMs) => {
        console.log('[PredictStore] setUpgradeReminderSkipUntil:', untilMs);
        set({ upgradeReminderSkipUntil: untilMs });
      },

      // debug:设置 CLOB host 覆盖(persist),需刷新页面生效
      setClobHostOverride: (url) => {
        console.log('[PredictStore] setClobHostOverride:', url);
        set({ clobHostOverride: url });
      },

      // V2 升级欢迎弹窗的"已看过"标记(persist),debug 可清掉重弹
      setV2WelcomeShown: (shown) => {
        console.log('[PredictStore] setV2WelcomeShown:', shown);
        set({ v2WelcomeShown: shown });
      },

      // 清除所有状态（登出时调用）
      clearAll: () => {
        console.log('[PredictStore] clearAll');
        set({
          credentials: null,
          credentialsExpireAt: null,
          feeConfig: null,
          version: null,
          versionUpdatedAt: null,
          usdcAllowances: [],
          ctfApprovals: [],
          approvalsLoading: false,
          approvalsError: null,
          pusdBalance: BigInt(0),
          usdcEBalance: BigInt(0),
          balancesUpdatedAt: null,
          balancesLoading: false,
          balancesError: null,
          upgradeReminderSkipUntil: null,
          clobHostOverride: null,
          v2WelcomeShown: false,
        });
      },
    }),
    {
      name: 'predict-store', // localStorage key
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        },
      },
      // 指定要持久化的字段（过滤掉方法）
      partialize: (state) => ({
        credentials: state.credentials,
        credentialsExpireAt: state.credentialsExpireAt,
        feeConfig: state.feeConfig,
        version: state.version,
        versionUpdatedAt: state.versionUpdatedAt,
        upgradeReminderSkipUntil: state.upgradeReminderSkipUntil,
        clobHostOverride: state.clobHostOverride,
        v2WelcomeShown: state.v2WelcomeShown,
      } as PredictState),
    }
  )
);

// 初始化时检查凭证是否过期
if (typeof window !== 'undefined') {
  const checkExpiredCredentials = () => {
    const state = usePredictStore.getState();
    if (state.credentials && state.credentialsExpireAt && Date.now() >= state.credentialsExpireAt) {
      console.log('[PredictStore] Credentials expired on init, clearing...');
      state.setCredentials(null);
    }
  };
  // 延迟执行，确保 store 已初始化
  setTimeout(checkExpiredCredentials, 0);
}

// 选择器
export const usePredictCredentials = () => usePredictStore((state) => state.credentials);

/**
 * 按 store.version 选当前展示用的"现金余额"(number,已 decimal-converted):
 *   V1 → USDC.e 余额
 *   V2 → pUSD 余额
 *
 * 任何想"展示用户可下单余额"的组件都用这个,**不要**再自己读 V1/V2 raw bigint。
 */
export const usePredictCashBalance = (): number => {
  return usePredictStore((s) => {
    const raw = s.version === 1 ? s.usdcEBalance : s.pusdBalance;
    return Number(raw) / 1e6;
  });
};
